/**
 * Uniform resampling helpers shared by the lag-based insight modules
 * (cross-signal correlation, ACF seasonality). Both need a series sampled on
 * an even time grid; raw datapoints are only *approximately* evenly spaced
 * (gaps, irregular aggregates), so we linearly interpolate onto a clean grid.
 */
import { median } from 'simple-statistics';

import type { TimeseriesPoint } from './types.ts';

/** Median spacing between consecutive samples, in ms. 0 for fewer than 2 points. */
export function medianStepMs(points: TimeseriesPoint[]): number {
  if (points.length < 2) {
    return 0;
  }
  const diffs = points.slice(1).map((p, i) => p.timestamp - (points[i]?.timestamp ?? p.timestamp));
  return median(diffs);
}

/**
 * Sample `points` onto a uniform grid by linear interpolation: `count` values
 * starting at `startMs`, spaced `stepMs` apart. Queries outside the data are
 * clamped to the nearest endpoint. Assumes `points` is sorted ascending by
 * timestamp; an empty series yields all-zero values.
 */
export function resampleUniform(points: TimeseriesPoint[], startMs: number, stepMs: number, count: number): number[] {
  const grid = Array.from({ length: count }, (_, k) => startMs + k * stepMs);
  return grid.map((t) => {
    // Last sample at or before `t` — the left bracket of the interpolation.
    const i = points.reduce((acc, p, idx) => (p.timestamp <= t ? idx : acc), -1);
    const left = points[Math.max(i, 0)];
    const right = points[i + 1];
    if (!left) {
      return 0;
    }
    if (!right || t <= left.timestamp) {
      return left.value;
    }
    const span = right.timestamp - left.timestamp;
    return span <= 0 ? left.value : left.value + ((t - left.timestamp) / span) * (right.value - left.value);
  });
}
